import React from "react";
import { Lead, Section, P, Bullets, Callout, CTA } from "../../../../blog/components";

export const title = "The UI problem of AI coding agents";
export const excerpt =
  "The terminal was built for one human typing one command at a time. AI agents turn it into a place where many semi-autonomous processes act at once, and our UI patterns have not caught up.";
export const heroAlt = "Moebius-style cinematic landscape at golden hour";
export const readingTime = "6 min read";

/** In-text footnote marker: a real link to the cited source (opens in a new tab). */
function Fn({ n }: { n: number }) {
  const ref = REFERENCES[n - 1];
  return (
    <sup style={{ fontSize: "0.72em", lineHeight: 0, marginLeft: 1 }}>
      <a
        href={ref ? ref.href : `#fn-${n}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#5ba8f5", textDecoration: "none", fontWeight: 600 }}
      >
        [{n}]
      </a>
    </sup>
  );
}

const REFERENCES: { text: string; href: string; label: string }[] = [
  {
    text: "OpenAI, “Codex CLI,” OpenAI Developers Documentation. Codex CLI is described as a local terminal coding agent that can read, change, and run code in the selected directory.",
    href: "https://developers.openai.com/codex/cli",
    label: "developers.openai.com/codex/cli",
  },
  {
    text: "Anthropic, “Agent SDK overview,” Claude Code Documentation. The Agent SDK builds agents that can read files, run commands, search the web, edit code, and reuse the tools, agent loop, and context management that power Claude Code.",
    href: "https://code.claude.com/docs/en/agent-sdk/overview",
    label: "code.claude.com/docs/en/agent-sdk/overview",
  },
  {
    text: "GitHub, “About Copilot coding agent,” GitHub Docs. Copilot coding agent can research a repository, plan an implementation, change code on a branch, let the developer review the diff, and open a pull request.",
    href: "https://docs.github.com/copilot/concepts/agents/coding-agent/about-coding-agent",
    label: "docs.github.com/copilot/…/about-coding-agent",
  },
  {
    text: "Git, “git-worktree Documentation,” official Git documentation. git worktree lets one repository support multiple working trees, allowing more than one branch to be checked out at a time.",
    href: "https://git-scm.com/docs/git-worktree",
    label: "git-scm.com/docs/git-worktree",
  },
  {
    text: "Anthropic, “Tool use overview,” Claude API Documentation. Tool use is framed as an agentic loop in which Claude decides when to call a tool and the application or Anthropic executes it depending on the tool type.",
    href: "https://platform.claude.com/docs/en/agents-and-tools/tool-use/overview",
    label: "platform.claude.com/docs/…/tool-use/overview",
  },
];

export default function Post() {
  return (
    <>
      <Lead>For a long time, the terminal was the simplest part of the developer workflow.</Lead>

      <P>
        You opened a shell, ran commands, watched output, switched directories, maybe split a pane,
        and moved on. It was a thin, fast surface for direct execution, not a full workspace.
      </P>
      <P>
        AI coding agents have changed that. They write code. More to the point, they now operate
        inside the same environment where developers do the most stateful work: running
        tests, reading logs, switching branches, starting servers, inspecting files, and debugging
        failures.
      </P>
      <P>
        That makes the terminal a natural home for them. OpenAI’s Codex CLI runs locally from the
        terminal and can read, change, and run code in a directory.<Fn n={1} /> Anthropic’s Claude
        Agent SDK builds agents that read files, run commands, and edit code through the same agent
        loop and context management behind Claude Code.<Fn n={2} /> GitHub’s Copilot coding agent
        researches a repository, plans, changes code on a branch, and opens a pull request.<Fn n={3} />
      </P>
      <P>
        So the terminal is no longer only where you type commands. It is becoming a place where
        several semi-autonomous processes act, pause, ask for approval, mutate files, and wait for you
        to re-enter the loop. Most of our UI patterns were built for human-driven command execution,
        not for supervising long-running agents.
      </P>

      <Section title="The old model: one command, one result">
        <P>
          Traditional terminal UX is linear. You type a command, the machine responds, you read the
          output, you decide the next command. Tabs, panes, and multiplexers gave us more places to
          run commands, not a new way to understand ongoing work.
        </P>
        <P>
          An agent session breaks that. It is not just output; it is a task with intent, context,
          state, permissions, and side effects. It is closer to a live work object than a terminal
          buffer. But we still treat it like text in a tab.
        </P>
      </Section>

      <Section title="The new model: many tasks, many contexts">
        <P>
          Once one agent is useful, you want several: one fixing a test, one exploring a refactor, one
          reviewing a pull request, one running an experiment on another branch.
        </P>
        <P>
          That is why Git worktrees matter. They let one repository keep multiple working trees, with
          more than one branch checked out at once.<Fn n={4} /> Agents act inside a filesystem, so if
          they share a checkout, server, port, or database, results turn ambiguous and the human has
          to reconstruct what happened. The practical workflow becomes spatial, even when the UI is
          not. You start thinking in areas:
        </P>
        <Bullets
          items={[
            "One branch, one server, one failing test.",
            "One agent on a conservative fix, another on an aggressive one.",
            "One preview, one log stream, one diff.",
          ]}
        />
        <P>That is no longer a terminal problem. It is a workspace problem.</P>
      </Section>

      <Section title="Tabs do not preserve intent">
        <P>
          A tab tells you a terminal exists. It rarely tells you why: what task it belongs to, which
          branch it is on, whether it changed files, whether it is safe to kill, or whether the agent
          inside is waiting for you. That is fine for a shell. It is not fine when the terminal holds
          an agent that has reasoned for ten minutes, edited six files, and is now asking to apply a
          risky migration.
        </P>
        <P>A useful agent workspace should answer, at a glance:</P>
        <Bullets
          items={[
            "What is this agent trying to do, and on what state?",
            "What changed, and what has it already tried?",
            "Is it blocked, waiting, failed, or still working?",
            "Can I compare two attempts, and resume tomorrow?",
          ]}
        />
        <P>
          And agent output is not a log. Some of it is decisions, assumptions, plans, and permission
          boundaries. A build log can be skimmed; an agent transcript has to be interpreted.
          Terminal-native agents like Codex CLI and Claude Code sit close to the real environment,
          <Fn n={1} /><Fn n={2} /> which makes them powerful but means the UI has to communicate more
          than text.
        </P>
      </Section>

      <Section title="Supervising is the hard part">
        <Callout>
          Launching an agent is easy. Supervising one is harder. Supervising several is much harder.
        </Callout>
        <P>
          A chat box next to a file tree works for small tasks. It strains once the work is
          operational and needs terminals, diffs, previews, test results, logs, and branches. Every
          mode trades off:
        </P>
        <Bullets
          items={[
            "An IDE agent is close to code, but not always to runtime.",
            "A terminal agent is close to runtime, but not always organized.",
            "A cloud pull-request agent like Copilot fits review, not live steering.",
          ]}
        />
        <P>
          Copilot is built around branch-based work and a reviewable diff;<Fn n={3} /> Codex CLI is
          terminal-local;<Fn n={1} /> the Claude Agent SDK exposes the same file, command, and
          code-editing loop programmatically.<Fn n={2} /> None of them is the whole answer. So the
          question is simple: what interface keeps a developer in control when useful work happens in
          more than one place?
        </P>
      </Section>

      <Section title="Expose state, not just streams">
        <P>
          A better workspace treats each task as a first-class object, not just a tab, transcript, or
          branch, carrying its instruction, working directory, branch or worktree, commands run, files
          changed, status, approvals, and your notes.
        </P>
        <P>
          This does not hide the terminal. The terminal is honest: it shows the real command surface
          and stays inspectable. The UI should not replace it; it should wrap it with memory,
          structure, and navigation, the way developers already arrange windows physically as external
          memory.
        </P>
        <P>
          Safety is part of this. “Allow command?” is weak if you do not know which task asked, what it
          has touched, and what it will change. The more agentic the tool, the more the UI must show
          provenance: who initiated this, from which instruction, on what state, and what can be
          rolled back. Anthropic frames the agentic loop as the model deciding to call a tool and the
          application or Anthropic executing it;<Fn n={5} /> a good interface makes those boundaries
          visible.
        </P>
      </Section>

      <Section title="The terminal won because it is composable">
        <P>
          The temptation is to build one giant IDE-shaped command center. That is probably the wrong
          instinct. The terminal lasted because it composes: it works with any language, framework,
          package manager, or half-broken local script, because it knows very little and lets other
          tools speak through it. So an agent workspace should be less an “AI IDE” and more a
          coordination surface:
        </P>
        <Bullets
          items={[
            "Terminals, branches, diffs, and logs stay real.",
            "Agents become visible as workers with state.",
            "The human stays the person who decides what matters.",
          ]}
        />
      </Section>

      <Section title="The real bottleneck is attention">
        <P>
          For years, developer tools optimized for typing speed and autocomplete. With agents, the
          bottleneck moves from keystrokes to attention: can I understand what is happening, trust this change, compare
          two attempts, resume tomorrow, kill the right process, and tell whether an agent is stuck or
          actually working?
        </P>
        <P>
          The next useful layer is probably not a smarter chat box but a workspace model that
          understands tasks over messages, worktrees over folders, agent state over output, approvals
          over prompts, and history over scrollback. The terminal will still be there, more than ever.
          But it may not be enough once the unit of work is bigger than a command.
        </P>
        <P>The old surfaces are starting to hide the most important thing:</P>
        <p style={{ margin: "8px 0 0", fontSize: 22, fontWeight: 600, letterSpacing: "-0.01em", color: "var(--text-primary)" }}>
          What is actually going on?
        </p>
      </Section>

      <Callout>
        CATE is our attempt at this coordination surface: terminals, editors, browsers, and agents as
        first-class windows on one infinite canvas.
      </Callout>
      <CTA label="Meet CATE" href="/" />

      <Section title="References">
        <ol style={{ margin: 0, paddingLeft: 22, display: "flex", flexDirection: "column", gap: 12 }}>
          {REFERENCES.map((r, i) => (
            <li key={i} id={`fn-${i + 1}`} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
              {r.text}{" "}
              <a href={r.href} target="_blank" rel="noopener noreferrer" style={{ color: "#5ba8f5", wordBreak: "break-word" }}>
                {r.label}
              </a>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}
